import React, { useState, useEffect, useMemo, useCallback } from "react";
import { ArrowLeft, Share2 } from "lucide-react";

const FLYER = "/flyer-launch-300.jpg";
const ZOOM_URL = "https://us06web.zoom.us/j/2480049812";
// Launch: June 13, 2026 — start 1:00 PM ET, ends 6:00 PM ET
const START_UTC = "2026-06-13T17:00:00Z"; // 13:00 ET → 17:00 UTC (EDT)
const END_UTC = "2026-06-13T22:00:00Z";   // 18:00 ET → 22:00 UTC (EDT)

function pad2(n) {
  return String(Math.max(0, Math.floor(n))).padStart(2, "0");
}
function shortZoneName(iana) {
  const map = {
    "America/New_York": "ET", "America/Detroit": "ET",
    "America/Indiana/Indianapolis": "ET", "America/Toronto": "ET",
    "America/Chicago": "CT", "America/Mexico_City": "CT", "America/Winnipeg": "CT",
    "America/Denver": "MT", "America/Phoenix": "MT", "America/Edmonton": "MT", "America/Boise": "MT",
    "America/Los_Angeles": "PT", "America/Vancouver": "PT", "America/Tijuana": "PT",
  };
  return map[iana] || null;
}
function formatLocal(when, iana) {
  const short = shortZoneName(iana);
  try {
    const day = new Intl.DateTimeFormat("en-US", {
      timeZone: iana, weekday: "long", month: "long", day: "numeric",
    }).format(when);
    const t = new Intl.DateTimeFormat("en-US", {
      timeZone: iana, hour: "numeric", minute: "2-digit",
      ...(short ? {} : { timeZoneName: "short" }),
    }).format(when);
    return short ? `${day} @ ${t} ${short}` : `${day} @ ${t}`;
  } catch (_) {
    return when.toLocaleString();
  }
}

function useNow() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

export default function Launch() {
  const start = useMemo(() => new Date(START_UTC), []);
  const end = useMemo(() => new Date(END_UTC), []);
  const now = useNow();
  const tz = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York";
    } catch (_) {
      return "America/New_York";
    }
  }, []);
  const localStr = useMemo(() => formatLocal(start, tz), [start, tz]);

  const isOver = now >= end;
  const isLive = !isOver && now >= start;
  const target = isLive ? end : start;
  const diff = Math.max(0, target.getTime() - now.getTime());
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);

  const openZoom = useCallback(() => {
    window.open(ZOOM_URL, "_blank", "noopener,noreferrer");
  }, []);

  const [copied, setCopied] = useState(false);
  const sharePage = useCallback(async () => {
    const pageUrl = window.location.href;
    const text = "Join the SHYFT Coalition Launch 300 — Sat, June 13 @ 1 PM ET.";
    if (navigator.share) {
      try {
        await navigator.share({ title: "SHYFT Launch 300", text, url: pageUrl });
        return;
      } catch (_) { /* fall through */ }
    }
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      const ta = document.createElement("textarea");
      ta.value = pageUrl; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (e) {}
      document.body.removeChild(ta);
    }
  }, []);

  return (
    <div
      className="min-h-screen w-full text-white overflow-x-hidden"
      style={{
        background:
          "radial-gradient(ellipse 70% 50% at 20% 0%, rgba(0,184,255,0.16) 0%, transparent 60%)," +
          "radial-gradient(ellipse 60% 50% at 90% 90%, rgba(201,169,110,0.12) 0%, transparent 60%)," +
          "linear-gradient(180deg, #0a1532 0%, #050505 100%)",
      }}
      data-testid="launch-page"
    >
      {/* Tiny top bar */}
      <header className="max-w-6xl mx-auto flex items-center justify-between px-6 lg:px-10 py-5">
        <a
          href="/"
          className="flex items-center gap-2.5 text-white/75 hover:text-white transition-colors text-sm"
          data-testid="back-link"
        >
          <ArrowLeft size={16} />
          <span className="tracking-wide">shyftclasses.com</span>
        </a>
        <button
          onClick={sharePage}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs tracking-[0.18em] text-white/80 hover:text-white border border-white/15 hover:border-[rgba(0,184,255,0.55)] transition"
          data-testid="share-button"
        >
          <Share2 size={14} />
          {copied ? "LINK COPIED" : "SHARE"}
        </button>
      </header>

      {/* Hero */}
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 lg:gap-12 items-center">
          {/* Flyer */}
          <div className="md:col-span-2 shyft-fade-up">
            <a
              href={ZOOM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="flyer-link"
              className="block rounded-3xl overflow-hidden shyft-special-banner group relative"
              aria-label="Open Zoom for SHYFT Coalition Launch 300"
            >
              <img
                src={FLYER}
                alt="SHYFT Coalition Launch 300 flyer"
                className="w-full h-auto block transition-transform duration-700 group-hover:scale-[1.02]"
                loading="eager"
              />
            </a>
          </div>

          {/* Right column */}
          <div className="md:col-span-3 shyft-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="flex items-center gap-3 mb-5">
              <span className="shyft-special-pill">SPECIAL EVENT</span>
              {isLive && (
                <span className="shyft-live-badge">
                  <span className="shyft-live-dot" />
                  LIVE
                </span>
              )}
            </div>

            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.03] text-white">
              SHYFT Coalition<sup className="shyft-tm">TM</sup>
              <br />
              Launch 300
            </h1>

            <p
              className="mt-6 text-lg md:text-2xl shyft-next-class-line"
              data-testid="launch-when"
            >
              {isOver
                ? "This Launch has ended"
                : isLive
                  ? "In Session Now — Join Live"
                  : localStr}
            </p>

            <p className="mt-5 text-white/75 text-base md:text-lg max-w-xl leading-relaxed">
              Our Official Launch hosted by Coach Andre, Coach Chase, and Coach Jo.
              It&rsquo;s time to Shift How You Financially Think.
            </p>

            {/* Countdown */}
            {!isOver && (
              <div
                className="mt-7 flex items-center gap-3 sm:gap-4"
                data-testid="launch-countdown"
              >
                <span
                  className="font-serif-soft italic text-sm lg:text-base mr-1"
                  style={{ color: "var(--shyft-gold)" }}
                >
                  {isLive ? "Ends in" : "Starts in"}
                </span>
                <CountUnit value={days} label="Days" />
                <Sep />
                <CountUnit value={hours} label="Hrs" />
                <Sep />
                <CountUnit value={mins} label="Min" />
                <Sep />
                <CountUnit value={secs} label="Sec" />
              </div>
            )}

            {/* CTA */}
            <div className="mt-9 flex flex-wrap gap-4">
              {isOver ? (
                <a
                  href="/"
                  className="shyft-cta-pill inline-flex items-center gap-3 px-9 py-4 text-sm font-semibold tracking-[0.22em]"
                  data-testid="back-home-button"
                >
                  <span className="shyft-dot" />
                  VIEW CLASS SCHEDULE
                </a>
              ) : (
                <button
                  onClick={openZoom}
                  className="shyft-cta-pill inline-flex items-center gap-3 px-10 py-4 text-base font-semibold tracking-[0.22em]"
                  data-testid="join-launch-button"
                >
                  <span className="shyft-dot" />
                  JOIN LAUNCH
                </button>
              )}
            </div>

            <p className="mt-6 text-white/45 text-xs tracking-wider">
              Zoom link: <a
                href={ZOOM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/65 hover:text-white underline underline-offset-4"
                data-testid="zoom-text-link"
              >us06web.zoom.us/j/2480049812</a>
            </p>
          </div>
        </div>
      </main>

      <footer className="max-w-6xl mx-auto px-6 lg:px-10 pb-10 text-center">
        <p className="text-white/40 text-xs tracking-wider">
          © {new Date().getFullYear()} SHYFT Coalition&trade; · All Rights Reserved.
        </p>
      </footer>
    </div>
  );
}

function CountUnit({ value, label }) {
  return (
    <div className="flex flex-col items-center">
      <div className="shyft-count-pill">{pad2(value)}</div>
      <div className="text-[0.55rem] sm:text-[0.6rem] tracking-[0.22em] mt-1.5 text-white/55 uppercase">
        {label}
      </div>
    </div>
  );
}
function Sep() {
  return (
    <span
      className="text-base sm:text-lg -mt-3 hidden sm:inline"
      style={{ color: "var(--shyft-gold)" }}
    >:</span>
  );
}
